.article {
  max-width: var(--measure);
  margin: 0 auto;
  padding: 4rem 1.5rem 8rem;
}

.head {
  margin-bottom: 3rem;
  text-align: center;
}

.back {
  display: inline-block;
  margin-bottom: 2.5rem;
  font-size: 0.8em;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-soft);
}

.head h1 {
  margin: 0;
  font-size: 2em;
  font-weight: 600;
  line-height: 1.15;
  text-wrap: balance;
}

.byline {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: baseline;
  gap: 0.5em;
  margin: 0.75rem 0 0;
  font-size: 0.8em;
  color: var(--ink-soft);
}

/*
 * The separator belongs to the item that follows it, so a missing date or an
 * unrevised article drops its dot with it and no rule has to know which
 * items are present. Every item is a span rather than the content itself,
 * which keeps the dot outside the revision link's underline and hit area.
 */
.byline > * + *::before {
  content: "·";
  margin-right: 0.5em;
}

/* An older revision says so before the reader is a paragraph into it. */
.superseded {
  margin: 1.5rem 0 0;
  font-size: 0.85em;
  font-style: italic;
  color: var(--ink-soft);
}


/*
 * Book setting: paragraphs run on with a first-line indent instead of being
 * separated by space, so the column reads as one block. The first paragraph
 * of the article and of each section is flush left, as it would be in print.
 */
/*
 * Run-on paragraphs with a first-line indent, as a book sets them. The
 * column is ragged rather than justified, so hyphenation is only there to
 * keep the rag from swinging too far.
 */
.prose p {
  margin: 0;
  text-indent: 1.5em;
  hyphens: auto;
}

.prose > p:first-child,
.prose > :is(h2, h3, h4, hr, blockquote, pre, ul, ol) + p {
  text-indent: 0;
}

.prose :is(h2, h3, h4) {
  margin: 2.5em 0 0.6em;
  font-weight: 600;
  line-height: 1.25;
  text-wrap: balance;
}

/*
 * Every section heading is a permalink. It reads as a heading — no
 * underline, no link colour — until it is hovered or focused, when the
 * accent comes up and a # appears out in the left gutter, clear of the
 * text so the line never shifts.
 */
.prose :is(h2, h3, h4) a {
  position: relative;
  text-decoration: none;
}

.prose :is(h2, h3, h4) a::before {
  content: "\00a7";
  position: absolute;
  right: 100%;
  margin-right: 0.35em;
  color: var(--accent);
  font-weight: 400;
  opacity: 0;
  transition: opacity 0.12s ease-in-out;
}

.prose :is(h2, h3, h4) a:hover,
.prose :is(h2, h3, h4) a:focus-visible {
  color: var(--accent);
}

.prose :is(h2, h3, h4) a:hover::before,
.prose :is(h2, h3, h4) a:focus-visible::before {
  opacity: 1;
}

.prose h2 {
  font-size: 1.35em;
}

.prose h3 {
  font-size: 1.1em;
}

.prose h4 {
  font-size: 1em;
  font-style: italic;
  font-weight: 400;
}

.prose :is(ul, ol) {
  margin: 1em 0;
  padding-left: 1.4em;
}

.prose li {
  margin: 0.35em 0;
  padding-left: 0.2em;
}

/*
 * A quote and a code listing are the same gesture — a passage set off from
 * the argument — so they share one rule down the left rather than each
 * inventing its own.
 */
.prose :is(blockquote, pre) {
  margin: 1.6em 0;
  padding: 0.2em 0 0.2em 1.2em;
  border-left: 2px solid var(--rule);
}

.prose blockquote {
  color: var(--ink-soft);
  font-size: 0.95em;
}

.prose blockquote p {
  text-indent: 0;
}

.prose hr {
  width: 6em;
  margin: 2.5em auto;
  border: 0;
  border-top: 1px solid var(--rule);
}

.prose :not(pre) > code {
  font-size: 0.95em;
}

/*
 * Code sits on the page rather than in a tinted box: no background, just a
 * rule down the left the way a printed listing is set off.
 */
.prose pre {
  overflow-x: auto;
  font-size: 0.9em;
  line-height: 1.5;
  /*
   * macOS draws overlay scrollbars only while no mouse is attached; with one
   * plugged in a listing gets a permanent grey gutter under it. Both the
   * track and the thumb start transparent so the bar is invisible at rest,
   * and the thumb tints when the block is hovered or focused — the space is
   * reserved either way, so nothing reflows when it appears.
   */
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
}

.prose pre:hover,
.prose pre:focus-within {
  scrollbar-color: var(--rule) transparent;
}

.prose pre::-webkit-scrollbar {
  height: 0.5em;
}

.prose pre::-webkit-scrollbar-track {
  background: transparent;
}

.prose pre::-webkit-scrollbar-thumb {
  background: transparent;
  border-radius: 0.25em;
}

.prose pre:hover::-webkit-scrollbar-thumb,
.prose pre:focus-within::-webkit-scrollbar-thumb {
  background: var(--rule);
}

/*
 * The highlighting palette. Shiki names these per token at build time, so
 * changing a syntax colour is an edit here rather than a rebuild of every
 * article — and one set of rules covers both themes.
 */
.prose :global(.shiki) {
  /*
   * Four steps down from the page ink rather than two. Punctuation used to
   * share the comment value and, at mono 0.9em, the braces and dots holding a
   * line together were the faintest thing on the page; it now sits one step
   * down, and only a comment — which is meant to recede — takes the lightest.
   */
  --code-ink: var(--ink);
  --code-comment: var(--accent);
  --code-soft: color-mix(in srgb, var(--ink) 80%, var(--paper));
  --code-dim: var(--ink-soft);
  --code-punct: var(--ink-soft);
  background: none !important;
}

/*
 * The way out of an article: the two around it and the way back to the list.
 * The middle column holds its place whether or not there is a neighbour on
 * either side, so Contents never drifts off centre.
 */
.nav {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: start;
  gap: 1.5rem;
  margin-top: 5rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--rule);
  font-size: 0.85em;
}

.navSide {
  display: flex;
  flex-direction: column;
  gap: 0.15em;
  text-decoration: none;
  text-wrap: balance;
}

.navNext {
  grid-column: 3;
  text-align: right;
}

.navLabel {
  font-size: 0.8em;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ink-soft);
}

.navToc {
  grid-column: 2;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-size: 0.8em;
  color: var(--ink-soft);
}

@media (max-width: 34rem) {
  /* Stacked on a phone: previous, contents, next, each on its own line. */
  .nav {
    grid-template-columns: 1fr;
    gap: 1rem;
    text-align: center;
  }

  .navSide,
  .navNext,
  .navToc {
    grid-column: 1;
    text-align: center;
    align-items: center;
  }
}

/*
 * The magazine end mark: a filled square closing the final paragraph, tied
 * to it with a non-breaking space so it can never be orphaned onto a line of
 * its own. It only appears when the article ends in prose — a piece ending
 * on a list or a code block gets none, as it would in print.
 */
.prose > p:last-child::after {
  content: "\00a0\25a0";
  color: var(--accent);
  font-size: 0.8em;
  vertical-align: 0.05em;
}

.prose img {
  max-width: 100%;
  height: auto;
}

.prose :is(h2, h3, h4):target {
  /* Anchored headings sit clear of the viewport edge. */
  scroll-margin-top: 2rem;
}
